import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { LogoMark } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { getSupabase } from "@/lib/supabase";

const Shell = ({ title, children, testId }) => (
  <div className="min-h-screen grid-bg flex items-center justify-center px-4" data-testid={testId}>
    <div className="panel w-full max-w-md p-8 space-y-5">
      <div className="mx-auto w-fit"><LogoMark size={48} /></div>
      <h1 className="text-2xl font-extrabold tracking-tight text-center">{title}</h1>
      {children}
      <Link to="/login" data-testid="back-to-login-link" className="block text-center text-xs text-primary">Back to sign in</Link>
    </div>
  </div>
);

export const ForgotPassword = () => {
  const { config } = useAuth();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (config?.auth_mode === "supabase") {
        const { error } = await getSupabase().auth.resetPasswordForEmail(email, { redirectTo: `${window.location.origin}/reset-password` });
        if (error) throw new Error(error.message);
      } else {
        await api.post("/auth/forgot-password", { email });
      }
      setSent(true);
    } catch (err) {
      toast.error(err?.response ? errMsg(err) : err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title="Reset your password" testId="forgot-password-page">
      {sent ? (
        <p data-testid="forgot-password-sent" className="text-sm text-muted-foreground text-center">If an account exists for that email, a reset link is on its way. It expires in 1 hour.</p>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <Input data-testid="forgot-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="h-12 rounded-xl" />
          <button data-testid="forgot-submit-button" disabled={busy} className="w-full h-12 rounded-full brand-gradient text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}Send reset link
          </button>
        </form>
      )}
    </Shell>
  );
};

export const ResetPassword = () => {
  const { config } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (config?.auth_mode === "supabase") {
        const { error } = await getSupabase().auth.updateUser({ password });
        if (error) throw new Error(error.message);
      } else {
        await api.post("/auth/reset-password", { token: params.get("token") || "", password });
      }
      toast.success("Password updated. Please sign in.");
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(err?.response ? errMsg(err) : err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title="Choose a new password" testId="reset-password-page">
      <form onSubmit={submit} className="space-y-3">
        <Input data-testid="reset-password-input" type="password" required minLength={8} maxLength={128} value={password} onChange={(e) => setPassword(e.target.value)}
          placeholder="New password (8+ chars, letter & number)" autoComplete="new-password" className="h-12 rounded-xl" />
        <button data-testid="reset-submit-button" disabled={busy} className="w-full h-12 rounded-full brand-gradient text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}Update password
        </button>
      </form>
    </Shell>
  );
};
